// external
import { useContext, useCallback, memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

// components
import CustomButton from "components/partials/custom-button/custom-button";
import CustomTextInput from "components/partials/custom-text-input/custom-text-input";
import RegimeLogicViewer from "./regime-logic-viewer";
import RegimeCellViewer from "./regime-cell-viewer";

// state managing/security
import { FaoRegimeContext } from "context/FaoRegimeContext";

// assets
import Icons from "assets/icons";

// css/styling
import { STYLES } from "../constants/styles.constants";
const MatrixName = memo(
  ({ matrix, isEditable, currentlyEditing, ind, onChange, cellMetrics }) => {
    if (!isEditable) {
      return (
        <div>
          <h2 className={`${STYLES.heading} pl-7`}>{matrix.name}</h2>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <CustomTextInput
          label="Matrix Name"
          id="matrixName"
          value={matrix.name}
          labelInTop={true}
          required={true}
          customClass={`${STYLES.label} pl-7`}
          inputCustomClass={STYLES.input}
          inputWrapperCustomClass="pl-7"
          onChange={onChange}
          disabled={currentlyEditing !== ind}
        />
      </div>
    );
  }
);

MatrixName.propTypes = {
  matrix: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
  isEditable: PropTypes.bool.isRequired,
  currentlyEditing: PropTypes.number.isRequired,
  ind: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

const MatrixEditButtons = memo(
  ({ isEditable, currentlyEditing, ind, onEdit, onDelete }) => {
    if (isEditable)
      return (
        <CustomButton
          size="md"
          iconBefore={<Icons.Delete aria-label="Delete Dimension" />}
          onClick={() => onDelete(ind)}
          customClass="text-blue-03"
        >
          Delete Matrix
        </CustomButton>
      );

    return (
      <div className="flex gap-4">
        <CustomButton
          size="md"
          iconBefore={<Icons.Configuring aria-label="Edit Matrix" />}
          onClick={() => onEdit(ind)}
          customClass={classNames("size-2 hover:text-blue-03 transition-all", {
            "text-blue-03": currentlyEditing === ind,
            "text-blue-01": currentlyEditing !== ind,
          })}
        >
          Edit
        </CustomButton>

        <CustomButton
          size="md"
          iconBefore={<Icons.Delete aria-label="Delete Dimension" />}
          onClick={() => onDelete(ind)}
          customClass="text-blue-03"
        >
          Delete Matrix
        </CustomButton>
      </div>
    );
  }
);

MatrixEditButtons.propTypes = {
  isEditable: PropTypes.bool.isRequired,
  currentlyEditing: PropTypes.number.isRequired,
  ind: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function RegimeViewer({ matrix, ind, isEditable, isDisplay, cellMetrics }) {
  const { currentlyEditing, setCurrentlyEditing, matrices, setMatrices } =
    useContext(FaoRegimeContext);
  const handleDuplicateMatrix = useCallback(() => {
    const matrixToDuplicate = matrices[ind];
    const duplicatedMatrix = JSON.parse(JSON.stringify(matrixToDuplicate));
    setMatrices([...matrices, duplicatedMatrix]);
  }, [matrices, ind, setMatrices]);

  const handleDeleteMatrix = useCallback(() => {
    const updatedMatrices = JSON.parse(JSON.stringify(matrices));
    updatedMatrices.splice(ind, 1);

    if (updatedMatrices.length === 0) {
      alert("Must have at least one matrix");
      return;
    }

    setMatrices(updatedMatrices);
    if (updatedMatrices.length === 1) {
      setCurrentlyEditing(0);
    }
  }, [matrices, ind, setMatrices, setCurrentlyEditing]);

  const handleEditMatrix = useCallback(() => {
    setCurrentlyEditing(ind);
  }, [ind, setCurrentlyEditing]);

  const onChangeMatrixName = useCallback(
    (event) => {
      const value = event.target.value;
      const updatedMatrices = JSON.parse(JSON.stringify(matrices));
      const currentMatrix = updatedMatrices[currentlyEditing];
      currentMatrix.name = value;
      updatedMatrices[currentlyEditing] = currentMatrix;
      setMatrices(updatedMatrices);
    },
    [matrices, currentlyEditing, setMatrices]
  );

  return (
    <div
      className={classNames(
        "mt-2 w-full flex-shrink rounded-md border p-5 transition-all ease-in-out",
        {
          "border-gray-07": isEditable,
          "cursor-not-allowed bg-gray-01": !isEditable,
        }
      )}
    >
      <div className="flex flex-col justify-start">
        {isEditable && (
          <p className="flex pl-7 font-semibold text-blue-03">Editing...</p>
        )}

        <div className="flex justify-between">
          <MatrixName
            matrix={matrix}
            isEditable={isEditable}
            currentlyEditing={currentlyEditing}
            ind={ind}
            onChange={onChangeMatrixName}
          />
          {!isDisplay && (
            <MatrixEditButtons
              isEditable={isEditable}
              currentlyEditing={currentlyEditing}
              ind={ind}
              onEdit={handleEditMatrix}
              onDelete={handleDeleteMatrix}
            />
          )}
        </div>

        <div>
          {matrix.logic.map((_, index) => (
            <div key={`logic-${index}`}>
              <RegimeLogicViewer
                matrixInd={ind}
                logicInd={index}
                isEditable={isEditable}
                isDisplay={isDisplay}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="pl-7">
        <div className="max-h-[500px] w-full overflow-x-auto">
          <RegimeCellViewer
            matrixInd={ind}
            isEditable={isEditable}
            curMatrix={matrix}
            isDisplay={isDisplay}
            cellMetrics={cellMetrics}
          />
        </div>

        {!isDisplay && (
          <CustomButton
            variant="secondary"
            size="md"
            iconBefore={<Icons.Edit aria-label="Edit Dimensions" />}
            onClick={handleDuplicateMatrix}
            customClass="mt-5"
          >
            Duplicate Matrix
          </CustomButton>
        )}
      </div>
    </div>
  );
}

RegimeViewer.propTypes = {
  matrix: PropTypes.shape({
    name: PropTypes.string.isRequired,
    logic: PropTypes.array.isRequired,
  }).isRequired,
  ind: PropTypes.number.isRequired,
  isEditable: PropTypes.bool.isRequired,
  isDisplay: PropTypes.bool.isRequired,
  cellMetrics: PropTypes.object.isRequired,
};

export default memo(RegimeViewer);
